import * as fs from 'fs';
import * as path from 'path';
import {FileSystemContract} from 'common/tool/FileSystemContract';
import {BadParameterException} from 'common/exception/BadParameterException';
import {asyncForEach} from 'common/util/PromiseUtil';
import {GeneratorUtil} from 'common/util/GeneratorUtil';

export class FileSystem implements FileSystemContract {
    public dirPath(filePath: string): string {
        const i: number = path.resolve(filePath).lastIndexOf(path.sep);
        if (i < 0) {
            return filePath;
        }

        return (path.resolve(path.resolve(filePath).substring(0, i + 1)) + path.sep).replace(/\\/g, '/');
    }

    public async listFiles(dirPath: string): Promise<string[]> {
        if (!this.isDir(dirPath)) {
            throw new BadParameterException(String(dirPath) + ' does not exist or is not a directory');
        }

        const separator: string = dirPath.endsWith('/') ? '' : '/';
        const files: string[] = fs.readdirSync(dirPath);

        const output: string[] = [];
        await asyncForEach(files, async (fileName: string) => {
            if (this.isFile(dirPath + separator + fileName)) {
                output.push(dirPath + separator + fileName);
            }
        });

        return output;
    }

    public async randomFile(dirPath: string, suggestion?: number): Promise<string> {
        const filenames: string[] = await this.listFiles(dirPath);
        if (filenames.length === 0) {
            return undefined;
        }

        let index: number;
        if (suggestion !== undefined) {
            index = Math.max(suggestion, 0) % filenames.length;
        } else {
            index = GeneratorUtil.int(0, filenames.length - 1);
        }

        return filenames[index];
    }

    public async move(filePath: string, dirPath: string): Promise<void> {
        if (!this.isFile(filePath)) {
            throw new BadParameterException(String(filePath) + ' does not exist or is not a file');
        }

        fs.mkdirSync(dirPath, {recursive: true});
        if (!this.isDir(dirPath)) {
            throw new BadParameterException(String(dirPath) + ' does not exist or is not a directory');
        }

        return fs.renameSync(filePath, dirPath + path.basename(filePath));
    }

    public async read(filePath: string): Promise<string> {
        if (!this.isFile(filePath)) {
            throw new BadParameterException(String(filePath) + ' does not exist or is not a file');
        }

        return fs.readFileSync(filePath, 'utf8');
    }

    public async readJson(jsonPath: string): Promise<any> {
        const data: any = await this.read(jsonPath);
        if (!this.isJson(data)) {
            throw new BadParameterException(String(jsonPath) + ' does not contain JSON');
        }

        return JSON.parse(data);
    }

    public async readJsonFiles(dirPath: string): Promise<{ name: string, data: any }[]> {
        if (!this.isDir(dirPath)) {
            throw new BadParameterException(String(dirPath) + ' does not exist or is not a directory');
        }

        const separator: string = dirPath.endsWith('/') ? '' : '/';
        const files: string[] = fs.readdirSync(dirPath);

        const output: { name: string, data: any }[] = [];
        await asyncForEach(files, async (fileName: string) => {
            if (this.isFile(dirPath + separator + fileName) && fileName.endsWith('.json')) {
                output.push({
                    name: fileName,
                    data: await this.readJson(dirPath + separator + fileName)
                });
            }
        });

        return output;
    }

    public async write(filePath: string, rawData: any): Promise<void> {
        let data: string = '';
        if (typeof rawData === 'string') {
            data = String(rawData);
        } else if (typeof rawData === 'number') {
            data = String(rawData);
        } else {
            data = JSON.stringify(rawData);
        }

        fs.mkdirSync(this.dirPath(filePath), {recursive: true});

        fs.writeFileSync(filePath, data, {encoding: 'utf8'});

        return Promise.resolve();
    }

    public async writeJson(jsonPath: string, data: any): Promise<void> {
        return this.write(jsonPath, JSON.stringify(data));
    }

    private isDir(dirPath: string): boolean {
        return fs.existsSync(dirPath) && fs.statSync(dirPath).isDirectory();
    }

    private isFile(filePath: string): boolean {
        return fs.existsSync(filePath) && fs.statSync(filePath).isFile();
    }

    private isJson(data: any): boolean {
        try {
            JSON.parse(data);

            return true;
        } catch (_error) {
            return false;
        }
    }
}
